import { IDataItem } from "../../../../../models/data.interface";
import { Util } from "../../../../../utils/util";
import { CustomizeItemModifierHalfBar } from "./customize-item-half-bar";
import { CustomizeItemUiOption, HalfModifierSelection } from "../../../../../types/enums";
import { CustomizeItemModifierBase } from "../base/customize-item-modifier-base";
import { ICustomizeItemModifierBaseElement } from "../base/customize-item-modifier-base.interface";
import { ICustomizeItemPremodifierBaseParams } from "../base/customize-item-premodifier-bar-base";
import { CustomizeItemPremodifierBarSingleItem } from "./customize-item-premodifier-bar";

export interface ICustomizeItemModifierSingleElement extends ICustomizeItemModifierBaseElement {
    preModifierId: number | null,
    halfId: HalfModifierSelection,
    menuItem: IDataItem,
    isSelected?: boolean
}

/**
 * Represents a modifier option within the customize item UI, including any optional helper bars
 * like premodifiers or halfable selections.
 */
export class CustomizeItemModifierSingleItem extends CustomizeItemModifierBase<ICustomizeItemModifierSingleElement> {
    /**
     * Creates a modifier option element for the UI.
     * 
     * @param {ICustomizeItemModifierElement} params - Configuration parameters for the modifier option.
     * @returns {HTMLElement} The constructed modifier option element.
     */
    protected _createModifierOption(params: ICustomizeItemModifierSingleElement): HTMLElement {
        const displayGroup = params.modifierDisplayGroup;

        const priceCaloriesId = displayGroup === null
            ? `spn_customize_item_modifier_size_price_cal_${params.modifierId}`
            : `spn_customize_item_modifier_price_cal_${displayGroup.ModifierDisplayGroupId}_${params.modifierId}`;

        let imageHtml = "";
        if (params.groupHasImage) {
            imageHtml = `
                <div class="customize-item-modifier-label-image">
                    ${(params.imageUrl !== "" && params.imageUrl !== null) ? `<img src="${params.imageUrl}" alt="" aria-hidden="true">` : ""}
                </div>`;
        }

        const isRadio = params.isSize
            || (((displayGroup?.MinSelectionCount === 1 && displayGroup?.MaxSelectionCount === 1)
                || displayGroup?.MustToggle)
                && !displayGroup?.IsWeightChangeable);

        const isSingleSelect = params.isSize
            || (displayGroup?.MinSelectionCount === 1 && displayGroup?.MaxSelectionCount === 1)
            || (displayGroup?.MustToggle ?? false);

        // role="option" and aria-selected go on the LABEL
        // aria-hidden="true" hides the native input from screen readers entirely
        const labelAriaAttrs = isSingleSelect
            ? `role="option" aria-selected="${params.isSelected ? 'true' : 'false'}" tabindex="${params.isSelected ? '0' : '-1'}"`
            : '';

        const inputAriaAttrs = isSingleSelect
            ? `aria-hidden="true" tabindex="-1"`
            : `aria-labelledby="${params.id}_name"`;

        const html = `
            <label id="lbl_${params.id}" class="option-row" ${labelAriaAttrs}>
                <div class="customize-item-modifier-label">
                    ${imageHtml}
                    <div class="customize-item-modifier-label-info">
                        <span id="${params.id}_name">${params.name}</span>
                        <span id="${priceCaloriesId}" class="price"></span>
                    </div>
                </div>
                <div class="custom-radio">
                    <input type="${isRadio ? 'radio' : 'checkbox'}" 
                           id="${params.id}" 
                           name="${params.radioId}"
                           ${inputAriaAttrs}
                           ${params.isSelected ? 'checked="checked"' : ''}>
                    <span class="checkmark"></span>
                </div>
                ${isSingleSelect ? `<span class="sr-only" data-state-label="true">${params.isSelected ? ' selected' : ''}</span>` : ''}
            </label>`;

        const label = Util.createHtmlElementFromTemplate(html);

        label.addEventListener("click", (event) => {
            event.preventDefault();
            event.stopPropagation();
            requestAnimationFrame(() => this._handleCheckboxActivation(params));
        });

        if (isSingleSelect) {
            label.addEventListener("keydown", (event: KeyboardEvent) => {
                if (event.key === " " || event.key === "Spacebar" || event.key === "Enter") {
                    event.preventDefault();
                    requestAnimationFrame(() => this._handleCheckboxActivation(params));
                }

                if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                    event.preventDefault();
                    const listbox = label.closest('[role="listbox"]');
                    if (!listbox) return;

                    // Filter out nested [role="option"] elements belonging to inner listboxes
                    // (e.g. SegmentedButton premodifier bars inside each option)
                    const options = Array.from(listbox.querySelectorAll<HTMLElement>('[role="option"]'))
                        .filter(opt => opt.closest('[role="listbox"]') === listbox);

                    const currentIndex = options.indexOf(label);
                    const nextIndex = event.key === "ArrowDown"
                        ? Math.min(currentIndex + 1, options.length - 1)
                        : Math.max(currentIndex - 1, 0);
                    if (nextIndex !== currentIndex) {
                        label.tabIndex = -1;
                        options[nextIndex].tabIndex = 0;
                        options[nextIndex].focus();
                    }
                }
            });
        } else {
            // Multi-select checkboxes: keyboard handler stays on the native input
            const checkbox = label.querySelector(`#${params.id}`) as HTMLInputElement | undefined;
            if (checkbox) {
                checkbox.onclick = null;
                checkbox.addEventListener("keydown", (event: KeyboardEvent) => {
                    if (event.key === " " || event.key === "Spacebar" || event.key === "Enter") {
                        event.preventDefault();
                        requestAnimationFrame(() => this._handleCheckboxActivation(params));
                    }
                });
            }
        }

        return label;
    }

    private readonly _handleCheckboxActivation = (params: ICustomizeItemModifierSingleElement): void => {
        const label = document.getElementById(`lbl_${params.id}`);

        if (label?.getAttribute("role") === "option") {
            const listbox = label.closest('[role="listbox"]');
            if (listbox) {
                listbox.querySelectorAll<HTMLElement>('[role="option"]').forEach(opt => {
                    opt.setAttribute("aria-selected", "false");
                    opt.tabIndex = -1;

                    // Clear sr-only state text on all siblings
                    const stateLabel = opt.querySelector("[data-state-label]");
                    if (stateLabel) stateLabel.textContent = "";
                });
            }

            label.setAttribute("aria-selected", "true");
            label.tabIndex = 0;

            // Set sr-only state text on selected item
            const stateLabel = label.querySelector("[data-state-label]");
            if (stateLabel) stateLabel.textContent = " selected";
        }

        if (params.modifierDisplayGroup === null)
            this._emitSizeClick(params.modifierId);
        else
            this._emitModifierClick(
                params.id,
                params.modifierDisplayGroup.ModifierDisplayGroupId,
                params.modifierId,
                HalfModifierSelection.WHOLE,
                params.isMultiSelect
            );
    }

    /**
     * Adds additional UI elements such as premodifiers and half-selection bars to the given modifier option.
     * 
     * @param {HTMLElement} optionDiv - The DOM element to append helper components to.
     */
    protected _addModifierAdditionalElements(
        optionDiv: HTMLElement,
        params: ICustomizeItemModifierSingleElement
    ): void {
        if (!params.modifierDisplayGroup || (!params.leftModifier && !params.rightModifier))
            return;

        const modifierId = params.leftModifier?.ModifierId ?? params.rightModifier?.ModifierId!;
        const preModifierExceptions = params.leftModifier?.PreModifierException ?? params.rightModifier?.PreModifierException!;

        if (params.modifierDisplayGroup.IsWeightChangeable) {
            const tempParams: ICustomizeItemPremodifierBaseParams = {
                modifierDisplayGroup: params.modifierDisplayGroup,
                modifierId: modifierId,
                isSelected: params.isSelected ?? false,
                preModifierId: params.preModifierId ?? this._data.getProperty("DefaultPreModifierId"),
                halfId: HalfModifierSelection.WHOLE,
                preModifierExceptions: preModifierExceptions,
                modifierName: params.name
            };

            const premodifierElement = new CustomizeItemPremodifierBarSingleItem(
                this._eventBus,
                this._data,
                this._context,
                this._localizationService,
                tempParams
            );

            if (premodifierElement.getPreModifierCount() > 1) {
                this._modules.premodifierBar = premodifierElement;

                if (premodifierElement.element)
                    optionDiv.appendChild(premodifierElement.element);
            }
        }

        if (params.modifierDisplayGroup.IsHalfable
            && params.menuItem.IsHalfable
            && this._data.getProperty("Settings").OOS.customizeItemMode === CustomizeItemUiOption.SINGLE_ITEM
        ) {
            const halfableElement = new CustomizeItemModifierHalfBar(
                this._eventBus,
                this._localizationService,
                params.modifierDisplayGroup.ModifierDisplayGroupId,
                modifierId,
                params.halfId,
                !params.isSelected
            );
            this._modules.halfBar = halfableElement;
            optionDiv.appendChild(halfableElement.element);
        }
    }
}